/**
 * Calculator Selector
 *
 * Gates one or more Calculator Containers on the same page. The author
 * sets a title + rich-text description + N buttons (label + Container ID); on a
 * fresh page load the selector is the only thing visible — every
 * `.calc-container[data-container-id]` is hidden until the visitor picks
 * a button and clicks Begin.
 *
 * Coordination with `calc-container`:
 *   • This component reads the URL hash (`cid=<id>;<lz-payload>`); when
 *     the encoded container ID matches one on the page, it pre-hides
 *     itself and reveals just that container so the prefilled review
 *     mode lands first thing. The matching `.calc-container` instance
 *     applies the LZ payload via its own `applyHashPayload()` — this
 *     component does NOT decompress the payload, only the framing.
 *   • The container fires `calc-selector:activate` from `showResultsView`
 *     and `enterPrefilledReviewMode` (see `notifyActivated()` in
 *     container.js); we listen so the selector hides itself even when
 *     activation is triggered after init (e.g. submit completes).
 *   • The container's results-page Start Over button fires
 *     `calc-selector:reopen`; we listen, hide every known container,
 *     and unhide ourselves so the visitor can pick a different
 *     calculator without refreshing.
 *
 * Edit mode: every container stays visible so authors can configure
 * each calculator's content. The visibility manipulation guards on
 * `body:not(.edit)` (matching the rest of the calculator codebase) so
 * authors never lose access to a hidden container.
 */
import { Component, registerComponent } from 'js/component.js';

const COMPONENT = 'calc-selector';

/* Mirror of the framing constants in container.js. We deliberately
   don't import them — that would couple the selector clientlib to the
   container clientlib at build time, and the only thing we need to
   parse here is the `cid=<id>;…` prefix. The LZ payload itself is
   opaque to this component. */
const HASH_CID_PREFIX = 'cid=';
const HASH_CID_SEP = ';';

export class CalculatorSelector extends Component {

    init() {
        this.isEditMode = document.body.classList.contains('edit');

        this.dom = {
            options: Array.from(this.element.querySelectorAll(`.${COMPONENT}__option`)),
            beginBtn: this.element.querySelector(`.${COMPONENT}__begin`),
        };

        this.containers = Array.from(document.querySelectorAll('.calc-container[data-container-id]'));
        this.knownIds = new Set(
            this.containers
                .map((c) => (c.getAttribute('data-container-id') || '').trim())
                .filter(Boolean),
        );

        this.bindOptionInteractions();
        this.bindBeginClick();
        this.syncBeginEnabled();

        document.addEventListener('calc-selector:activate', (e) => this.handleActivate(e));
        document.addEventListener('calc-selector:reopen', (e) => this.handleReopen(e));

        if (this.isEditMode) return;

        const targetId = CalculatorSelector.parseHashTargetId(window.location.hash);
        if (targetId && this.knownIds.has(targetId)) {
            /* Share-URL prefill path: the matching container's own
               `applyHashPayload()` will reveal it + render the review
               page. We hide every other container and stay hidden
               ourselves. */
            this.hideAllContainersExcept(targetId);
            this.hideSelector();
            return;
        }

        this.hideAllContainers();
        this.showSelector();
    }

    bindOptionInteractions() {
        /* Single click handler — selects the card under the cursor and
           clears any sibling that was previously selected. Same code
           runs on keydown below so mouse and keyboard land on the
           same selection state. */
        const selectOption = (option) => {
            if (!option) return;
            const isAlreadySelected = option.getAttribute('aria-checked') === 'true';
            if (isAlreadySelected) return;

            this.dom.options.forEach((other) => {
                const checked = other === option;
                other.classList.toggle(`${COMPONENT}__option--selected`, checked);
                other.setAttribute('aria-checked', checked ? 'true' : 'false');
            });
            this.syncBeginEnabled();
        };

        this.dom.options.forEach((option) => {
            option.addEventListener('click', () => selectOption(option));
            /* The card is a `<div role="radio">` (not a native button or
               radio input), so the browser does NOT fire `click` on
               Space / Enter — we intercept those keys ourselves. The
               ARIA radio pattern (MDN: `role="radio"`) requires both
               Space and Enter to activate. */
            option.addEventListener('keydown', (e) => {
                if (e.key !== ' ' && e.key !== 'Enter' && e.key !== 'Spacebar') return;
                e.preventDefault();
                selectOption(option);
            });
        });
    }

    getSelectedContainerId() {
        const selected = this.dom.options.find(
            (o) => o.getAttribute('aria-checked') === 'true',
        );
        return selected ? (selected.getAttribute('data-container-id') || '').trim() : '';
    }

    /**
     * Begin is disabled until a card is selected AND the chosen
     * Container ID actually exists on the page. Authors who target a
     * non-existent Container ID end up with a permanently disabled
     * Begin button, which is the right failure mode — better a
     * dead-looking CTA than one that silently does nothing on click.
     */
    syncBeginEnabled() {
        if (!this.dom.beginBtn) return;
        const targetId = this.getSelectedContainerId();
        const valid = !!targetId && this.knownIds.has(targetId);
        this.dom.beginBtn.disabled = !valid;
    }

    bindBeginClick() {
        if (!this.dom.beginBtn) return;
        this.dom.beginBtn.addEventListener('click', () => {
            /* The button is disabled until a real container ID is
               selected (see `syncBeginEnabled`), so the only way we get
               here is with a valid pick — guard anyway in case the
               button gets re-enabled by external code. */
            const targetId = this.getSelectedContainerId();
            if (!targetId || !this.knownIds.has(targetId)) return;

            /* Blur before hiding the selector: otherwise the focused
               Begin button disappears and the browser may move focus to
               the next tab-stop (often deep in the calculator / page),
               which scrolls the viewport toward the bottom. */
            if (document.activeElement && this.element.contains(document.activeElement)) {
                document.activeElement.blur();
            }

            const revealed = this.revealContainer(targetId);
            this.hideSelector();

            /* After layout settles, scroll the **window** to the document top.
               `scrollIntoView(revealed)` aligned the `.calc-container` with the
               viewport, which visually parked the first accordion section at
               the top; authors expect the literal top of the page (same as a
               fresh load) after Begin. */
            if (revealed) {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                    });
                });
            }
        });
    }

    /* ───── Container visibility ───── */

    hideAllContainers() {
        this.containers.forEach((c) => { c.hidden = true; }); // eslint-disable-line no-param-reassign
    }

    hideAllContainersExcept(targetId) {
        this.containers.forEach((c) => {
            const isTarget = c.getAttribute('data-container-id') === targetId;
            c.hidden = !isTarget; // eslint-disable-line no-param-reassign
        });
    }

    /**
     * Unhide the matching `.calc-container` and hide the rest.
     * @returns {HTMLElement|null} the revealed container element
     */
    revealContainer(targetId) {
        let revealed = null;
        this.containers.forEach((c) => {
            const isTarget = c.getAttribute('data-container-id') === targetId;
            c.hidden = !isTarget; // eslint-disable-line no-param-reassign
            if (isTarget) revealed = c;
        });
        return revealed;
    }

    /* ───── Selector visibility ───── */

    hideSelector() {
        this.element.hidden = true;
    }

    showSelector() {
        this.element.hidden = false;
        this.clearSelection();
    }

    clearSelection() {
        this.dom.options.forEach((option) => {
            option.classList.remove(`${COMPONENT}__option--selected`);
            option.setAttribute('aria-checked', 'false');
        });
        this.syncBeginEnabled();
    }

    /* ───── Cross-component events ───── */

    handleActivate(event) {
        const containerId = event && event.detail && event.detail.containerId;
        if (!containerId || !this.knownIds.has(containerId)) return;
        this.hideAllContainersExcept(containerId);
        this.hideSelector();
    }

    handleReopen(event) {
        if (this.isEditMode) return;
        const containerId = event && event.detail && event.detail.containerId;
        /* If the firing container isn't one we manage, leave the page
           alone. */
        if (containerId && !this.knownIds.has(containerId)) return;
        this.hideAllContainers();
        this.showSelector();
        this.element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    /* ───── Helpers ───── */

    static parseHashTargetId(hash) {
        const cleaned = (hash || '').replace(/^#/, '');
        if (!cleaned.startsWith(HASH_CID_PREFIX)) return '';
        const sepIdx = cleaned.indexOf(HASH_CID_SEP);
        if (sepIdx <= HASH_CID_PREFIX.length) return '';
        return cleaned.slice(HASH_CID_PREFIX.length, sepIdx);
    }
}

registerComponent(`.${COMPONENT}`, CalculatorSelector);
